// 유사기업 조회의 "지난 조회" 목록 — 버핏 저장 목록(buffettRecent)과 같은 방식.
//
// 진실 소스는 서버다: 완료된 조회마다 valuation-2 가 peer_recent 테이블에
// (rid·입력 요약·개사 수·시각)을 남기고 /agent/peer/recent 로 내 목록을 준다 —
// 기기를 옮겨도 같은 목록. localStorage(사용자별 키)는 완료 직후 즉시 표시와
// 오프라인 폴백용이고, 화면은 둘을 rid 로 합쳐 그린다. 항목을 누르면 rid
// 딥링크(/peer?tab=…&rid=…)로 영구 스냅샷을 복원한다.
//
// 버핏과의 차이: 버핏은 종목 단위로 덮어쓰지만(같은 종목 재분석 = 갱신) peer 는
// 같은 입력이라도 그날그날 결과가 달라 실행마다 별개 기록이다 — rid 단위로
// 한 건씩 쌓고 덮어쓰지 않는다.
import { useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "../stores/auth";

export interface PeerRecent {
  rid: string;
  tab: "ai" | "industry" | "precedent";
  /** 공시 선례 — 결과는 DB 갱신으로 달라져 저장하지 않고, 검색 조건만 남겨 열 때 다시 검색한다 */
  precedent?: PrecedentQuery;
  /** 입력 요약 — ai: 사업내용, industry: 선택한 분류 목록 */
  label: string;
  /** 최종 선정 개사 수 */
  count?: number;
  created: string;   // ISO
}

const LIMIT = 40;

export type PrecedentQuery = { query: string; year_from?: number | null; year_to?: number | null; evaluator?: string };

/** 같은 조건은 같은 기록(다시 검색하면 맨 위로 올라온다) — 조건 문자열의 해시 */
export function precedentRid(q: PrecedentQuery): string {
  const key = `${q.query.trim().toLowerCase()}|${q.year_from ?? ""}|${q.year_to ?? ""}|${(q.evaluator || "").trim().toLowerCase()}`;
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < key.length; i += 1) {
    h1 = Math.imul(h1 ^ key.charCodeAt(i), 16777619) >>> 0;
    h2 = Math.imul(h2 + key.charCodeAt(i), 2246822519) >>> 0;
  }
  return `pt${h1.toString(36)}${h2.toString(36)}`.slice(0, 40);
}

/** 지난 조회 → 다시 검색할 주소 조건(q·yf·yt·ev) */
export function precedentSearchQuery(q: PrecedentQuery): string {
  const p = new URLSearchParams({ q: q.query || "" });
  if (q.year_from) p.set("yf", String(q.year_from));
  if (q.year_to) p.set("yt", String(q.year_to));
  if (q.evaluator?.trim()) p.set("ev", q.evaluator.trim());
  return p.toString();
}

export function precedentLabel(q: PrecedentQuery): string {
  const years = q.year_from || q.year_to ? ` · ${q.year_from ?? ""}~${q.year_to ?? ""}` : "";
  const ev = q.evaluator?.trim() ? ` · ${q.evaluator.trim()}` : "";
  return `${q.query.trim() || "(키워드 없음)"}${years}${ev}`;
}

/** 공시 선례 검색 기록 — 로컬은 맨 위로 갱신, 서버(/agent/user-docs/peer_precedent)에도 남겨 기기를 옮겨도 보이게 */
export function recordPrecedentSearch(q: PrecedentQuery, count: number): void {
  const rid = precedentRid(q);
  try {
    const list = peerRecentList().filter((x) => x.rid !== rid);
    const entry: PeerRecent = { rid, tab: "precedent", label: precedentLabel(q), count, precedent: q, created: new Date().toISOString() };
    localStorage.setItem(storeKey(), JSON.stringify([entry, ...list].slice(0, LIMIT)));
    if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT));
  } catch { /* 기록 실패가 검색 흐름을 막지 않는다 */ }
  void api(`/agent/user-docs/peer_precedent/${rid}`, {
    method: "PUT",
    body: JSON.stringify({ name: q.query.trim() || q.evaluator || "공시 선례", payload: { ...q, result_count: count } }),
  }).catch(() => { /* 서버 저장소가 없으면 로컬만 */ });
}
const EVENT = "va:peer-recent";

function storeKey(): string {
  try {
    const u = useAuth.getState().user as Record<string, unknown> | null;
    const k = String(u?.uid || u?.username || u?.email || "_anon").toLowerCase();
    return `va_peer_recent::${k}`;
  } catch {
    return "va_peer_recent::_anon";
  }
}

export function peerRecentList(): PeerRecent[] {
  try {
    return JSON.parse(localStorage.getItem(storeKey()) || "[]") as PeerRecent[];
  } catch {
    return [];
  }
}

export function recordPeerRecent(r: Omit<PeerRecent, "created">): void {
  try {
    if (!r.rid) return;
    const list = peerRecentList();
    if (list.some((x) => x.rid === r.rid)) return;   // rid 는 실행마다 고유 — 재기록 없음
    const next = [{ ...r, created: new Date().toISOString() }, ...list];
    localStorage.setItem(storeKey(), JSON.stringify(next.slice(0, LIMIT)));
    if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT));
  } catch {
    /* 기록 실패가 조회 흐름을 막지 않는다 */
  }
}

interface ServerRecent {
  rid: string;
  kind: string;          // peer_ai | industry
  label: string;
  count?: number | null;
  created_epoch: number; // UTC 초 — DATETIME 문자열은 타임존이 없어 epoch 로 받는다
}

type PrecedentDoc = { id: string; payload?: (PrecedentQuery & { result_count?: number | null }) | null; updated?: string; created?: string };

async function fetchServerPrecedents(): Promise<PeerRecent[]> {
  try {
    const d = await api<{ ok: boolean; data?: PrecedentDoc[] }>("/agent/user-docs/peer_precedent");
    if (!d.ok || !Array.isArray(d.data)) return [];
    return d.data.flatMap((doc) => {
      const q = doc.payload;
      if (!q || typeof q !== "object") return [];
      const query: PrecedentQuery = { query: String(q.query || ""), year_from: q.year_from ?? null, year_to: q.year_to ?? null, evaluator: q.evaluator || "" };
      return [{ rid: doc.id, tab: "precedent" as const, label: precedentLabel(query), precedent: query,
                count: typeof q.result_count === "number" ? q.result_count : undefined, created: doc.updated || doc.created || "" }];
    });
  } catch {
    return [];
  }
}

async function fetchServerRecent(): Promise<PeerRecent[]> {
  const [peers, precedents] = await Promise.all([fetchServerPeerRecent(), fetchServerPrecedents()]);
  return [...peers, ...precedents];
}

async function fetchServerPeerRecent(): Promise<PeerRecent[]> {
  try {
    const d = await api<{ ok: boolean; items?: ServerRecent[] }>("/agent/peer/recent");
    if (!d.ok || !Array.isArray(d.items)) return [];
    return d.items
      .filter((x) => x.rid && (x.kind === "peer_ai" || x.kind === "industry"))
      .map((x) => ({
        rid: x.rid,
        tab: (x.kind === "peer_ai" ? "ai" : "industry") as PeerRecent["tab"],
        label: x.label || "",
        count: typeof x.count === "number" ? x.count : undefined,
        created: new Date((x.created_epoch || 0) * 1000).toISOString(),
      }));
  } catch {
    return [];   // 서버가 안 닿으면 로컬 목록만으로 그린다
  }
}

/** rid 기준 병합 — 서버가 진실 소스(시각·개사 수), 서버에 아직 없는 방금 완료분은
 *  로컬이 메운다. 최신순 정렬 후 상한. */
export function mergeRecent(server: PeerRecent[], local: PeerRecent[]): PeerRecent[] {
  const seen = new Set(server.map((x) => x.rid));
  const all = [...server, ...local.filter((x) => !seen.has(x.rid))];
  all.sort((a, b) => (a.created < b.created ? 1 : a.created > b.created ? -1 : 0));
  return all.slice(0, LIMIT);
}

/** 목록 훅 — 로컬 즉시 + 서버 병합. 완료 기록 이벤트마다 서버도 다시 읽는다. */
export function usePeerRecent(): PeerRecent[] {
  const [list, setList] = useState<PeerRecent[]>(peerRecentList);
  useEffect(() => {
    let alive = true;
    let server: PeerRecent[] = [];
    const paint = () => {
      if (alive) setList(mergeRecent(server, peerRecentList()));
    };
    const pull = () => {
      void fetchServerRecent().then((rows) => { server = rows; paint(); });
    };
    const sync = () => { paint(); pull(); };
    pull();
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);   // 다른 탭에서의 기록도 반영
    return () => {
      alive = false;
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return list;
}
